import { useEffect, useState } from 'react';

/**
 * Tracks whether a horizontally-scrollable container is clipped at its start/end edges — used to fade the
 * table's left/right scroll shadows in and out. Recomputes on scroll and on size changes of the container
 * or any of its children.
 */
export function useOverflowX(container: HTMLElement | null): { start: boolean; end: boolean } {
  const [overflow, setOverflow] = useState({ start: false, end: false });
  useEffect(() => {
    if (!container) {
      return;
    }
    const update = (): void => {
      const start = container.scrollLeft > 0;
      const end = container.scrollLeft + container.clientWidth < container.scrollWidth - 1;
      setOverflow((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(container);
    for (const child of container.children) {
      ro.observe(child);
    }
    container.addEventListener('scroll', update, { passive: true });
    return () => {
      ro.disconnect();
      container.removeEventListener('scroll', update);
    };
  }, [container]);
  return overflow;
}
